import { useContext } from 'react';
import { HttpContext } from '../context/HttpContext';
import sentry from '../sentry';

export const useMfaApi = () => {
  const { authAxios } = useContext(HttpContext);

  const handleApiError = (error, context = 'MFA operation') => {
    sentry.sendError(error);
    console.error(`${context}:`, error);
    if (error.response?.status && !error.status) {
      error.status = error.response.status;
    }
    return error;
  };

  const setupMfa = async (otpMethod) => {
    try {
      const response = await authAxios.post('/internal/v1/mfa/setup', {
        otp_method: otpMethod,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'MFA setup');
    }
  };

  const verifyOtp = async (otpAttempt) => {
    try {
      const response = await authAxios.post('/internal/v1/mfa/verify', {
        otp_attempt: otpAttempt,
      });
      return response;
    } catch (error) {
      throw handleApiError(error, 'OTP verification');
    }
  };

  const lockAccount = async () => {
    try {
      const response = await authAxios.post('/internal/v1/mfa/lock-account');
      return response;
    } catch (error) {
      throw handleApiError(error, 'Account locking');
    }
  };

  const sendEmailCode = async () => {
    try {
      const response = await authAxios.post('/internal/v1/mfa/send-email-code');
      return response;
    } catch (error) {
      throw handleApiError(error, 'Email code sending');
    }
  };

  const fetchRecoveryCodes = async () => {
    try {
      const response = await authAxios.post('/internal/v1/mfa/backup-codes');
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Recovery codes fetching');
    }
  };

  const disableMfa = async () => {
    try {
      const response = await authAxios.post('/internal/v1/mfa/disable');
      return response;
    } catch (error) {
      throw handleApiError(error, 'MFA disable');
    }
  };

  const reEnableMfa = async () => {
    try {
      const response = await authAxios.post('/internal/v1/mfa/re-enable');
      return response;
    } catch (error) {
      throw handleApiError(error, 'MFA re-enable');
    }
  };

  const resetAuthenticator = async () => {
    try {
      const response = await authAxios.post(
        '/internal/v1/mfa/reset-otp-secret'
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Reset authenticator');
    }
  };

  const regenerateRecoveryCodes = async () => {
    try {
      const response = await authAxios.post(
        '/internal/v1/mfa/backup-codes/regenerate'
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Regenerate recovery codes');
    }
  };

  const revertOtpSetup = async (otpMethod, otpSecret) => {
    try {
      const response = await authAxios.post(
        '/internal/v1/mfa/revert-otp-setup',
        {
          otp_method: otpMethod,
          otp_secret: otpSecret,
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Revert OTP setup');
    }
  };

  const verifyRecoveryCode = async (recoveryCode) => {
    try {
      const response = await authAxios.post(
        '/internal/v1/mfa/backup-codes/verify',
        { backup_code: recoveryCode }
      );
      return response;
    } catch (error) {
      throw handleApiError(error, 'Consume recovery code');
    }
  };

  return {
    setupMfa,
    verifyOtp,
    lockAccount,
    sendEmailCode,
    fetchRecoveryCodes,
    disableMfa,
    reEnableMfa,
    resetAuthenticator,
    regenerateRecoveryCodes,
    revertOtpSetup,
    verifyRecoveryCode,
  };
};
